import * as Accordion from '@radix-ui/react-accordion';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { useMedia } from 'react-use';

import { LIMIT_ORDER_SUPPORTED_CHAINS, NETWORK_NAME_PER_CHAIN_ID } from '@/constants/chain';

import { LIMIT_ORDER_MAX_EXPIRATION_DAYS } from '@/store/limitOrder';

import { fontStyles } from '@/styles/typography';
import { breakpoints } from '@/styles/util';

import { ChainAvatar } from '@/ui/Avatars/ChainAvatar';

import { StyledH2, StyledParagraph, StyledPrimaryCell } from '../utils';
import { AccordionItem } from './AccordionItem';

const getImage = (num: number) => `/images/limit-orders/steps/step-${num}.png`;

const stepItems = [
  {
    title: 'Select your Buy and Sell tokens',
    subtitle: 'Click on the dropdown to select your tokens',
    image: getImage(1),
  },
  {
    title: 'Set the price and timeline',
    subtitle: `Enter a price and set an expiration up to ${LIMIT_ORDER_MAX_EXPIRATION_DAYS} days`,
    image: getImage(2),
  },
  {
    title: 'Approve and submit',
    subtitle: 'Grant token approval then submit your order',
    image: getImage(3),
  },
  {
    title: 'Wait until your price hits',
    subtitle: 'Takers can fill your order in full or in parts',
    image: getImage(4),
  },
  {
    title: 'Check order history',
    subtitle: 'Find completed, open and cancelled orders',
    image: getImage(5),
  },
  {
    title: 'Cancel anytime',
    subtitle: 'Cancelling limit orders costs a small gas fee',
    image: getImage(6),
  },
];

export function HowLimitOrdersWorkSection() {
  const [open, setOpen] = useState<string>(stepItems[0].title);
  const isMobile = useMedia(breakpoints.mobile, false);

  useEffect(() => {
    const timer = setInterval(() => {
      const currentIdx = stepItems.findIndex(({ title }) => title === open);
      const nextStep = currentIdx < stepItems.length - 1 ? currentIdx + 1 : 0;
      setOpen(stepItems[nextStep].title);
    }, 4000);

    return () => clearInterval(timer);
  }, [open]);

  const handleOnValueChange = (value: string) => {
    setOpen(value);
  };

  const selectedItem = stepItems.find(({ title }) => title === open) ?? stepItems[0];

  return (
    <StyledPrimaryCell className="flex-col items-center pb-14.5 desktop:pb-7.5 pt-14.5 desktop:pt-13 pl-6 tablet:pl-15 pr-6 tablet:pr-6.5">
      <StyledH2 className="text-center">How to setup limit orders</StyledH2>
      <StyledParagraph className="text-center">
        Available on {LIMIT_ORDER_SUPPORTED_CHAINS.length} chains:{' '}
      </StyledParagraph>
      <ul className="mt-4 mb-16 tablet:mb-24 desktop:mb-13.5 flex gap-3 flex-wrap justify-center">
        {LIMIT_ORDER_SUPPORTED_CHAINS.map((chain) => (
          <li
            key={chain.name}
            className={fontStyles({
              variant: 'BODY_LG',
              class: ['text-token-gray-800', 'flex', 'items-center', 'gap-2'],
            })}
          >
            <ChainAvatar chainId={chain.id} size={20} />
            <span>{NETWORK_NAME_PER_CHAIN_ID[chain.id]}</span>
          </li>
        ))}
      </ul>
      <div className="flex flex-col desktop:flex-row desktop:justify-between items-center w-full">
        <Accordion.Root
          type="single"
          defaultValue={stepItems[0].title}
          className="desktop:w-full mr-4"
          onValueChange={handleOnValueChange}
          value={open}
        >
          {stepItems.map((props, idx) => (
            <AccordionItem
              key={idx}
              idx={idx}
              itemsLength={stepItems.length}
              open={open}
              {...props}
            />
          ))}
        </Accordion.Root>
        <Image
          src={selectedItem.image}
          alt=""
          width={isMobile ? 391 : 558}
          height={isMobile ? 406 : 485}
          className="max-w-full h-auto mt-10 tablet:mt-24 desktop:mt-0"
        />
      </div>
    </StyledPrimaryCell>
  );
}
